import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Stethoscope, Layers, Target, Lock, Sparkles, AlertOctagon, TrendingUp } from "lucide-react";
import Logo from "@/components/Logo";

const Stat = ({ value, label }) => (
  <div className="border-l border-gold/30 pl-5">
    <div className="font-display text-4xl text-gold tabular-nums">{value}</div>
    <div className="overline text-ink-muted mt-1">{label}</div>
  </div>
);

const Step = ({ n, icon: Icon, title, body }) => (
  <div className="relative border border-gold/15 bg-navy-800/60 p-7" data-testid={`step-${n}`}>
    <div className="overline text-gold/80 mb-4">Step 0{n}</div>
    <Icon className="h-6 w-6 text-gold mb-4" strokeWidth={1.25} />
    <h3 className="font-display text-xl text-ink mb-2">{title}</h3>
    <p className="text-sm text-ink-muted leading-relaxed">{body}</p>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-navy-900 text-ink">
      {/* Top bar — sticky */}
      <header className="sticky top-0 z-40 border-b border-gold/10 bg-navy-900/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="w-24" />
          <nav className="hidden md:flex items-center gap-10 text-sm text-ink-muted">
            <a href="#how" className="hover:text-ink transition">How it works</a>
            <a href="#why" className="hover:text-ink transition">Why ScaleIQ</a>
            <a href="#deliverable" className="hover:text-ink transition">The Report</a>
            <a href="#pricing" className="hover:text-ink transition">Pricing</a>
            <a href="#beyond" className="hover:text-ink transition">Beyond</a>
            <a href="#faq" className="hover:text-ink transition">FAQ</a>
          </nav>
          <Link to="/assessment" data-testid="nav-start-button" className="text-sm bg-gold text-navy-900 px-5 py-2.5 font-semibold hover:bg-gold-400 transition-colors">Start Assessment</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1773053525984-3857c1e0be44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9uJTIwZ29sZHxlbnwwfHx8fDE3ODE5ODg1Mzh8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.9), transparent 80%)",
            WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,0.9), transparent 80%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-28 md:pt-20 md:pb-36">
          <div className="flex flex-col items-center text-center mb-14 md:mb-20 animate-fade-up" data-testid="hero-logo-block">
            <img
              src="/assets/scaleiq-logo.png"
              alt="ScaleIQ"
              draggable="false"
              className="h-48 md:h-64 lg:h-72 w-auto select-none drop-shadow-[0_0_60px_rgba(212,175,55,0.15)]"
            />
            <div className="overline text-gold mt-4 flex items-center gap-3">
              <span className="h-px w-10 bg-gold" />
              The AI Readiness Assessment
              <span className="h-px w-10 bg-gold" />
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8 animate-fade-up">
              <h1 className="font-display font-light text-5xl md:text-7xl leading-[1.02] tracking-tight">
                Is your business actually<br />
                <span className="text-gold italic">ready for AI?</span>
              </h1>
              <p className="mt-8 text-lg text-ink-muted max-w-2xl leading-relaxed">
                Before you spend another dollar on ChatGPT, agents, or an "AI consultant" — take our 5-minute diagnostic. We'll show you where AI will make you more profitable, and where your real problem isn't AI at all.
              </p>
              <div className="mt-8 border-l-2 border-gold pl-5 max-w-2xl">
                <p className="text-ink text-base leading-relaxed">
                  <span className="text-gold font-semibold">AI can't fix chaos.</span> It amplifies whatever process it touches. ScaleIQ diagnoses your operation first, then tells you where AI creates measurable ROI.
                </p>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link to="/assessment" data-testid="hero-cta-button" className="inline-flex items-center gap-3 bg-gold text-navy-900 px-7 py-4 font-semibold hover:bg-gold-400 transition-all">
                  Take the Free AI Readiness Assessment
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <div className="text-sm text-ink-muted flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-gold" /> 28 questions · ~5 minutes · free preview
                </div>
              </div>
            </div>
            <div className="md:col-span-4 hidden md:block space-y-7">
              <Stat value="0–100" label="AI Readiness Score" />
              <Stat value="6" label="Departments Diagnosed" />
              <Stat value="3" label="Free Insights On Completion" />
              <Stat value="$97" label="Unlocks Full Diagnostic" />
            </div>
          </div>
        </div>
        <div className="gold-hairline max-w-7xl mx-auto" />
      </section>

      {/* WHY ScaleIQ — the positioning section */}
      <section id="why" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <div className="overline text-gold mb-3">Why ScaleIQ</div>
            <h2 className="font-display text-4xl md:text-5xl font-light leading-tight">You don't have<br /><span className="text-gold italic">an AI problem.</span></h2>
          </div>
          <div className="md:col-span-7 space-y-5 self-end">
            <p className="text-ink-muted text-lg leading-relaxed">
              You probably have a <span className="text-ink">follow-up problem</span>. Or an <span className="text-ink">operations bottleneck</span>. Or <span className="text-ink">inconsistent sales</span>. Or a <span className="text-ink">handoff problem</span> between your team and your customers.
            </p>
            <p className="text-ink-muted text-lg leading-relaxed">
              AI amplifies good systems. Layered onto broken processes, it automates dysfunction — faster, at scale. Most consultants start with the tools. ScaleIQ starts with the business.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          <div className="border border-bad/30 bg-navy-800/60 p-7" data-testid="why-card-0">
            <AlertOctagon className="h-6 w-6 text-bad mb-4" strokeWidth={1.25} />
            <div className="overline text-bad mb-2">The Typical Path</div>
            <h3 className="font-display text-xl mb-2">Buy an AI tool. Hope it works.</h3>
            <p className="text-sm text-ink-muted leading-relaxed">Owners subscribe to ChatGPT, an "AI agent," a voice bot — with no diagnosis of the real leak. Six months later they've spent $10K+ with no ROI.</p>
          </div>
          <div className="border border-warn/30 bg-navy-800/60 p-7" data-testid="why-card-1">
            <Layers className="h-6 w-6 text-warn mb-4" strokeWidth={1.25} />
            <div className="overline text-warn mb-2">The Missed Diagnosis</div>
            <h3 className="font-display text-xl mb-2">The problem was never AI.</h3>
            <p className="text-sm text-ink-muted leading-relaxed">It was a 45-minute lead response, no follow-up sequence, or scheduling running through text messages. Fix that, and you unlock revenue AI could never touch.</p>
          </div>
          <div className="border border-gold bg-navy-800 p-7 relative" data-testid="why-card-2">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gold" />
            <Stethoscope className="h-6 w-6 text-gold mb-4" strokeWidth={1.25} />
            <div className="overline text-gold mb-2">The ScaleIQ Way</div>
            <h3 className="font-display text-xl mb-2">Diagnose first. Automate second.</h3>
            <p className="text-sm text-ink-muted leading-relaxed">Our proprietary engine measures operational maturity across 6 departments, surfaces the real gaps, then prescribes where AI creates measurable ROI in <span className="text-ink">your</span> business.</p>
          </div>
        </div>
      </section>

      <div className="gold-hairline max-w-7xl mx-auto" />

      {/* HOW IT WORKS — 3 steps */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="overline text-gold mb-3">Our Methodology</div>
          <h2 className="font-display text-4xl md:text-5xl font-light">Six categories.<br /><span className="text-gold italic">One view of the business.</span></h2>
          <p className="text-ink-muted text-lg leading-relaxed mt-5">A proprietary consulting engine — not another AI tool. In 5 minutes we diagnose your operation across six departments and show you where AI belongs in <span className="text-ink">your</span> business.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <Step n={1} icon={Stethoscope} title="Diagnose the business" body="28 targeted questions across Lead Management, Sales, Operations, Marketing, Administration, and Business Profile. We measure operational maturity — not tools." />
          <Step n={2} icon={Layers} title="Score the process" body="You get an AI Readiness Score (0–100) plus a category-by-category breakdown. This is where the real leaks are — before automation." />
          <Step n={3} icon={Target} title="Prescribe where AI fits" body="Only after we understand your business do we show where AI creates ROI — and where you shouldn't touch it. Every recommendation tied to dollar impact." />
        </div>
      </section>

      <div className="gold-hairline max-w-7xl mx-auto" />

      {/* DELIVERABLE */}
      <section id="deliverable" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="overline text-gold mb-3">The Deliverable</div>
            <h2 className="font-display text-4xl md:text-5xl font-light leading-tight mb-6">Free score + preview. <span className="text-gold italic">$97 for the full diagnostic.</span></h2>
            <p className="text-ink-muted text-lg leading-relaxed mb-6">
              Your free AI Readiness Score, a six-category view of your business, three things you're doing well, and three biggest process gaps.
            </p>
            <p className="text-ink-muted text-lg leading-relaxed mb-8">
              For <span className="text-gold font-semibold">$97</span> you unlock the full Executive Diagnostic: process maturity + AI opportunity scores across 6 departments, the 5 costliest bottlenecks, tech direction, and a 12-month revenue forecast — plus a <span className="text-gold">$200 credit</span> toward your Strategy Session + Blueprint.
            </p>
            <Link to="/assessment" data-testid="deliverable-cta-button" className="inline-flex items-center gap-3 border border-gold text-gold px-6 py-3 font-medium hover:bg-gold hover:text-navy-900 transition-colors">
              Get My Free Score <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="md:col-span-7">
            <div className="border border-gold/20 bg-navy-800 p-6 md:p-8 relative" data-testid="hvac-sample-card">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gold" />
              <div className="overline text-gold mb-2 flex items-center gap-2 flex-wrap"><Sparkles className="h-3 w-3 flex-shrink-0" /> <span>Preview · sample</span></div>
              <h3 className="font-display text-2xl mb-1">Peak Comfort HVAC</h3>
              <p className="text-sm text-ink-muted mb-6">HVAC · $1M–$3M revenue · 6–15 employees</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 mb-6">
                <div>
                  <div className="overline text-ink-muted mb-1">AI Readiness</div>
                  <div className="font-display text-4xl text-gold tabular-nums leading-none">62</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="overline text-ink-muted mb-1">Top Bottleneck</div>
                  <div className="font-display text-base text-ink leading-snug">Lead response averages 45 min. ~38% of qualified leads lost before AI ever gets involved.</div>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 bg-ok mt-2 flex-shrink-0" /><span className="text-ink-muted">Strong reputation asset (280+ Google reviews) — ready to amplify</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 bg-warn mt-2 flex-shrink-0" /><span className="text-ink-muted">Sales follow-up is ad-hoc — fix process before adding AI SDR</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 bg-bad mt-2 flex-shrink-0" /><span className="text-ink-muted">Scheduling still on text/phone — automate this before anything else</span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gold/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-ink-muted">
                <span className="flex items-center gap-2"><Lock className="h-3 w-3 flex-shrink-0" /> Full diagnostic unlocks at $97</span>
                <span>$200 credit toward next step</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="gold-hairline max-w-7xl mx-auto" />

      {/* PRICING */}
      <section id="pricing" className="max-w-5xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <div className="overline text-gold mb-3">Pricing</div>
          <h2 className="font-display text-4xl md:text-5xl font-light">A diagnostic — not a subscription.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-px bg-gold/15 border border-gold/15">
          <div className="bg-navy-800 p-10" data-testid="pricing-free">
            <div className="overline text-ink-muted mb-3">The Diagnostic</div>
            <div className="font-display text-5xl mb-4">Free</div>
            <ul className="space-y-3 text-sm text-ink-muted mb-6">
              <li>· 28-question business diagnostic</li>
              <li>· Your AI Readiness Score (0–100)</li>
              <li>· Category-by-category process breakdown</li>
              <li>· Top 3 strengths + top 3 process gaps</li>
              <li>· Preview of the locked recommendations</li>
            </ul>
            <Link to="/assessment" data-testid="pricing-free-cta" className="inline-flex items-center gap-2 text-gold border border-gold px-5 py-2.5 hover:bg-gold hover:text-navy-900 transition">Start Free <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="bg-navy-800 p-10 relative" data-testid="pricing-pro">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gold" />
            <div className="overline text-gold mb-3">Full Executive Diagnostic</div>
            <div className="font-display text-5xl mb-4">$97 <span className="text-base text-ink-muted">/ one-time</span></div>
            <ul className="space-y-3 text-sm text-ink-muted mb-6">
              <li className="text-ink">· Everything in Free, plus:</li>
              <li>· Executive summary with monthly savings + revenue opportunity</li>
              <li>· 12-month revenue forecast (do-nothing vs strategy vs blueprint)</li>
              <li>· Process maturity + AI opportunity scores across 6 departments</li>
              <li>· The 5 costliest bottlenecks with dollar impact</li>
              <li>· High-level technology direction</li>
              <li>· Downloadable PDF deliverable</li>
              <li className="text-gold">★ $200 credit toward your Strategy Session + Transformation Blueprint (if booked within 7 days)</li>
            </ul>
            <Link to="/assessment" data-testid="pricing-pro-cta" className="inline-flex items-center gap-3 bg-gold text-navy-900 px-6 py-3 font-semibold hover:bg-gold-400 transition">Begin My Diagnostic <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <div className="gold-hairline max-w-7xl mx-auto" />

      {/* Beyond the Report — credibility, no pricing */}
      <section id="beyond" className="max-w-5xl mx-auto px-6 py-24">
        <div className="text-center mb-10">
          <div className="overline text-gold mb-3">Beyond the diagnostic</div>
          <h2 className="font-display text-4xl md:text-5xl font-light">Need more than a report?</h2>
        </div>
        <p className="text-ink-muted text-lg leading-relaxed text-center max-w-3xl mx-auto mb-12">
          When you're ready to move from <span className="text-ink">what's broken</span> to <span className="text-gold">what to do about it</span> — ScaleIQ offers strategy sessions, implementation blueprints, and warm intros to AI partners.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="border border-gold bg-navy-800 p-7 relative" data-testid="beyond-strategy-blueprint">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gold" />
            <div className="flex items-baseline justify-between mb-3">
              <div className="overline text-gold">The Full Playbook</div>
              <div className="font-display text-2xl text-gold tabular-nums">from $799</div>
            </div>
            <h3 className="font-display text-xl mb-3">Executive Strategy Session <span className="text-ink-muted">+</span> AI Transformation Blueprint</h3>
            <p className="text-sm text-ink-muted leading-relaxed mb-4">
              A live 60-minute working session with our team — review your diagnostic, prioritize bottlenecks, watch live tool demos — plus your complete implementation playbook: week-by-week sequence, vendors, stack, automation map, AI prompts, SOPs, and ROI model.
            </p>
            <div className="text-xs text-gold uppercase tracking-widest">
              ★ $200 credit if booked within 7 days of your diagnostic — <span className="text-ink">$599 net</span>
            </div>
          </div>
          <div className="border border-gold/15 bg-navy-800 p-7" data-testid="beyond-partners">
            <div className="overline text-gold mb-3">Step Into Execution</div>
            <h3 className="font-display text-xl mb-3">Implementation Partners</h3>
            <p className="text-sm text-ink-muted leading-relaxed">Warm introductions to vetted partners that specialize in businesses like yours. You are the architect. They are the builders.</p>
          </div>
        </div>
        <div className="mt-10 text-center text-sm text-ink-muted">
          Pricing revealed at each step. Start with the diagnostic — the ladder unlocks from there.
        </div>
      </section>

      <div className="gold-hairline max-w-7xl mx-auto" />

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 py-24">
        <div className="overline text-gold mb-3 text-center">FAQ</div>
        <h2 className="font-display text-4xl md:text-5xl font-light text-center mb-14">Common questions</h2>
        <div className="space-y-px">
          {[
            ["Is this another AI tool?", "No. ScaleIQ is a proprietary consulting engine that measures operational maturity across six departments, then tells you where AI belongs — and where it doesn't. Think MRI, not medicine cabinet."],
            ["Why do you say AI can't fix chaos?", "AI amplifies whatever process it touches. Layered onto a broken lead workflow, an AI receptionist just misses leads faster. Fix the process, then automate — same tool becomes a growth engine."],
            ["What's in the free version?", "Your AI Readiness Score (0–100), a category-by-category breakdown, top 3 strengths, and top 3 process gaps. Enough to know where you stand."],
            ["What's in the $97 full diagnostic?", "Executive summary with dollar impact, six-department process maturity + AI opportunity scores, the 5 costliest bottlenecks, tech direction, and a 12-month revenue forecast."],
            ["Where does the 'how' come from?", "The $97 report tells you WHAT is costing you money. The Executive Strategy Session walks through it live and prioritizes what to do first. A Transformation Blueprint or trusted partners handle the build."],
            ["Do I get a free strategy call with the $97 report?", "No — instead, a $200 credit toward your Strategy Session when you book within 7 days. Real value tied to real progress."],
            ["Who is this for?", "Small business owners who know AI matters but don't know where to start — including those already dabbling in ChatGPT. Not for enterprises with in-house AI teams."],
            ["Will you tell me where NOT to use AI?", "Yes. Often the most valuable part of the report — AI in the wrong department is one of the fastest ways to burn cash in 2026."],
            ["Is my data shared?", "No. Your responses are only used to generate your report — never sold or shared. See our Privacy Policy."],
            ["How do I get in touch?", "Email Kevin directly at kevin@tryscaleiq.com — we read every message."],
          ].map(([q, a], i) => (
            <details key={i} className="group border-t border-gold/10 py-6" data-testid={`faq-item-${i}`}>
              <summary className="flex justify-between items-center cursor-pointer list-none">
                <span className="font-display text-lg">{q}</span>
                <span className="text-gold group-open:rotate-45 transition-transform text-2xl font-light">+</span>
              </summary>
              <p className="text-ink-muted mt-3 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-navy-800 border-t border-gold/10">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="overline text-gold mb-3 flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-gold" /> One question. One diagnostic. <span className="h-px w-10 bg-gold" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-light mb-6">
            Before you invest another dollar in AI,<br />
            <span className="text-gold italic">find out where it actually belongs.</span>
          </h2>
          <Link to="/assessment" data-testid="footer-cta-button" className="inline-flex items-center gap-3 bg-gold text-navy-900 px-8 py-4 font-semibold hover:bg-gold-400 transition-colors">
            Take the AI Readiness Assessment <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-5 text-xs text-ink-muted flex items-center justify-center gap-2">
            <TrendingUp className="h-3 w-3 text-gold" /> Diagnose first · automate second · scale profitably
          </div>
        </div>
      </section>

      <footer className="border-t border-gold/10 py-12">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8 items-start">
          <div>
            <Logo size="xl" />
          </div>
          <div className="text-sm text-ink-muted">
            <div className="overline text-gold mb-3">Contact</div>
            <a href="mailto:kevin@tryscaleiq.com" data-testid="footer-contact-email" className="text-ink hover:text-gold transition-colors">
              kevin@tryscaleiq.com
            </a>
            <p className="mt-2 text-xs">All inquiries, partnerships, and support.</p>
          </div>
          <div className="text-sm text-ink-muted md:text-right">
            <div className="overline text-gold mb-3">Navigate</div>
            <div className="space-y-1">
              <a href="#why" className="block hover:text-ink">Why ScaleIQ</a>
              <a href="#how" className="block hover:text-ink">How it works</a>
              <a href="#deliverable" className="block hover:text-ink">The Report</a>
              <a href="#pricing" className="block hover:text-ink">Pricing</a>
              <a href="#faq" className="block hover:text-ink">FAQ</a>
              <Link to="/privacy" data-testid="footer-privacy-link" className="block hover:text-ink">Privacy Policy</Link>
              <Link to="/terms" data-testid="footer-terms-link" className="block hover:text-ink">Terms of Service</Link>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-10 pt-6 border-t border-gold/10 flex flex-col md:flex-row justify-between text-xs text-ink-muted gap-2">
          <span>© {new Date().getFullYear()} ScaleIQ · Diagnose first. Automate second.</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-ink">Privacy</Link>
            <Link to="/terms" className="hover:text-ink">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
